// src/project/data/config/apiConfig.js
import Storage from '@/shared/utils/storage'

export const API_BASE_URL = 'https://api.monrapidoo.com'

/**
 * Retourne le userId depuis le storage.
 */
export function getUserId() {
  const userData = Storage.get('userData')
  return userData?.id || null
}

/**
 * Retourne le riderId depuis le storage.
 * Source unique de vérité : userData.metadata.riderId (sauvegardé via l'API user update)
 */
export function getRiderId() {
  const userData = Storage.get('userData')
  return userData?.metadata?.riderId || null
}

/**
 * Construit une URL complète vers l'API V3 avec group=ultraxeno.
 * @param {string} endpoint - Chemin de l'endpoint (ex: '/rider/details/xyz')
 * @param {Object} params - Paramètres additionnels à ajouter en query string
 */
export function buildUrlWithAuth(endpoint, params = {}) {
  const url = new URL(`${API_BASE_URL}${endpoint}`)
  url.searchParams.append('group', 'ultraxeno')
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, value)
    }
  })
  return url.toString()
}

/**
 * Construit une URL de base pour les actions POST/PATCH sans paramètres supplémentaires.
 * @param {string} endpoint
 */
export function buildActionUrl(endpoint) {
  const separator = endpoint.includes('?') ? '&' : '?'
  return `${API_BASE_URL}${endpoint}${separator}group=ultraxeno`
}

/**
 * @deprecated — Plus utilisé en V3. Gardé pour compatibilité si besoin transitoire.
 */
export function buildResourceUrl(endpoint, resourceId, params = {}) {
  const endpointPath = endpoint
    .replace(':id', resourceId)
    .replace(':orderId', resourceId)
    .replace(':deliveryId', resourceId)
  return buildUrlWithAuth(endpointPath, params)
}

/**
 * @deprecated — Plus utilisé en V3. L'userId doit être passé explicitement.
 */
export function addUserIdToBody(body = {}) {
  const userId = getUserId()
  return { ...body, userId }
}

export const RIDER_ENDPOINTS = {
  // --- Wallet ---
  // GET — Solde du livreur. Appeler avec l'userId.
  BALANCE: userId =>
    `/wallet/balance/wallet_${userId}?service=rapidoo&country=ANY&processor=rapidoWallet&currency=XOF`,

  COMMISSION_STATS: userId =>
    `/stats/${userId}/month?service=rapidoo&type=commission`,
  WITHDRAWAL_STATS: userId =>
    `/stats/${userId}/month?service=rapidoo&type=withdrawal`,

  // --- Rider Profile ---
  // GET — Profil complet du livreur
  PROFILE: riderId => `/rider/details/${riderId}`,
  // POST — Créer un nouveau profil livreur (lié à un userId existant)
  RIDER_ADD: '/rider/add',
  // POST — Bascule online/offline (aucun body nécessaire)
  TOGGLE_STATUS: riderId => `/rider/toggle/status/${riderId}`,
  // POST — Mise à jour de la position GPS
  UPDATE_LOCATION: riderId => `/rider/update/location/${riderId}`,
  // GET — Recherche du profil par userId pour récupérer le riderId
  SEARCH_BY_USER: userId => `/rider/search/riders?userId=${userId}`,

  // --- KYC ---
  // GET — Statut et détails du KYC par son kycId
  KYC_DETAILS: kycId => `/kyc/details/${kycId}`,
  // POST — Création d'un brouillon KYC (step 1)
  KYC_CREATE_DRAFT: '/kyc/create/draft',
  // POST — Mise à jour des données KYC (step 2): URL doit inclure kycId
  KYC_UPDATE: kycId => `/kyc/update/${kycId}`,
  // POST — Soumission du KYC pour validation (step 3)
  KYC_SUBMIT: kycId => `/kyc/submit/${kycId}`,
  // Alias pour compatibilité (utilisé dans saveKyc.js)
  KYC_ADD: '/kyc/create/draft',

  // --- Produits (Boutique) ---
  // GET — Liste des équipements disponibles à l'achat
  PRODUCT_LIST: '/product/list',
  // GET — Liste des achats effectués par le livreur
  PRODUCT_BUYINGS: '/product/list/buyings',
  // POST — Réserver un équipement (crée un buying avec statut "pending")
  PRODUCT_BUY: '/product/create/buying',
  // POST — Confirmer un achat après paiement
  PRODUCT_CONFIRM_BUY: '/product/confirm/buying',
  // POST — Payer un achat via wallet ou mobile money (queue broadcast)
  PRODUCT_PAY:
    'https://api.monrapidoo.com/queue/broadcast/data/rapidoo-transactions/productPaymentRequest?group=commons',

  // --- Wallet / Transactions ---
  // GET — Historique des transactions du livreur
  WALLET_TRANSACTIONS: userId => `/rapidoo-transactions/list?userId=${userId}`,

  // --- Packages (Colis particulier-à-particulier) ---
  // GET — Tous les colis disponibles
  PACKAGE_LIST: '/package/list',
  // POST — S'assigner un colis (documenté dans API-packages-documentation.md)
  PACKAGE_ASSIGN: '/package/assign/rider',
  // POST — Mettre à jour le statut d'un colis
  PACKAGE_STATUS: '/package/update/status',
  // POST — Ajouter une entrée à la timeline
  PACKAGE_TIMELINE: '/package/update/timeline',

  // --- Orders (Commandes Restaurant) ---
  // GET — Toutes les commandes
  ORDER_LIST: '/order/list',
  // POST — S'assigner une commande (utilise le body de requête)
  ORDER_ASSIGN: '/order/assign',
  // POST — Mettre à jour le statut d'une commande (picked_up, delivered)
  ORDER_STATUS: `/order/update/status`,
  ORDER_TIMELINE: `/order/update/timeline`,
}
